// Bulk Import — turns what the reader (analyze-import) found into an import plan:
// which car each document belongs to, which cars need creating, which documents are
// the same document twice, and which MOT pages (certificate + result + payment slip)
// belong together. Pure functions only, so it can be tested without the app.

export const CATEGORY_LABELS = {
  title_deed: 'Title Deed',
  insurance: 'Insurance',
  road_tax: 'Road Tax',
  mot: 'MOT',
  classic_id: 'Classic ID',
  immobilization: 'Immobilization',
  expense: 'Expense',
  registration_foreign: 'Foreign registration',
  import: 'Import & purchase',
  history: 'Vehicle history',
  other: 'Other document',
  blank: 'Blank page (skip)',
};
export const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS);

export const normReg = (s) => String(s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
const normVin = (s) => String(s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// Where each category ends up, given what was read. Records that need a date the
// reader didn't find fall back to the Documents page rather than failing.
export const targetFor = (item) => {
  const f = item.fields || {};
  switch (item.category) {
    case 'title_deed': return 'title_deeds';
    case 'insurance': return f.expiry_date ? 'insurance_records' : 'documents';
    case 'road_tax': return f.expiry_date ? 'road_tax_records' : 'documents';
    case 'mot': return (f.report_date || f.next_due_date) ? 'mechanical_reports' : 'documents';
    case 'classic_id': return 'classic_id';
    case 'immobilization': return 'immobilization';
    case 'expense': return (!f.is_credit_note && Number(f.amount) > 0) ? 'expenses' : 'documents';
    case 'blank': return 'skip';
    default: return 'documents';
  }
};
export const TARGET_LABELS = {
  title_deeds: 'Title Deed', insurance_records: 'Insurance', road_tax_records: 'Road Tax',
  mechanical_reports: 'Mechanical / MOT', classic_id: 'Classic ID', immobilization: 'Immobilization',
  expenses: 'Expenses', documents: 'Documents page', skip: 'Skipped',
};

const isClassicYear = (y) => y && Number(y) <= new Date().getFullYear() - 25;

// Build the new-car proposal from the best documents for it.
const proposalFrom = (reg, docs) => {
  const rank = { title_deed: 0, registration_foreign: 1, mot: 2 };
  const src = [...docs].filter(d => d.vehicle && Object.keys(d.vehicle).length)
    .sort((a, b) => (rank[a.category] ?? 9) - (rank[b.category] ?? 9));
  const v = {};
  for (const d of src) for (const [k, val] of Object.entries(d.vehicle)) if (v[k] === undefined && val !== '' && val != null) v[k] = val;
  const vin = docs.map(d => normVin(d.vin)).find(x => x.length >= 8) || '';
  const mileages = docs.filter(d => d.category === 'mot').map(d => Number(d.fields?.mileage || d.vehicle?.mileage_km)).filter(n => n > 0);
  const td = docs.find(d => d.category === 'title_deed');
  const notes = [
    td?.fields?.certificate_number && `Cyprus registration certificate No ${td.fields.certificate_number}.`,
    v.first_registration_date && `First registered ${v.first_registration_date}.`,
    v.cyprus_registration_date && `First registered in Cyprus ${v.cyprus_registration_date}.`,
    !td && 'No Cyprus title deed in this import.',
    'Created by Bulk Import.',
  ].filter(Boolean).join(' ');
  const year = v.year || (v.first_registration_date ? Number(v.first_registration_date.slice(0, 4)) : '');
  return {
    key: 'n:' + reg,
    create: true,
    registration_number: reg,
    brand: v.make || '',
    model: v.model || '',
    variant: v.variant || '',
    body_style: v.body || '',
    year: year || '',
    color: v.colour || '',
    fuel_type: ['Petrol', 'Diesel', 'Electric', 'Hybrid', 'LPG'].includes(v.fuel) ? v.fuel : 'Petrol',
    vin,
    engine_number: v.engine_number || '',
    engine_size: v.engine_cc ? `${(v.engine_cc / 1000).toFixed(1)}L (${v.engine_cc}cc)` : '',
    mileage: mileages.length ? Math.max(...mileages) : (v.mileage_km || ''),
    status: isClassicYear(year) ? 'classic' : 'active',
    notes,
  };
};

const mostCommon = (arr) => {
  const c = {}; let best = '', n = 0;
  for (const x of arr) if (x) { c[x] = (c[x] || 0) + 1; if (c[x] > n) { n = c[x]; best = x; } }
  return best;
};

/**
 * docs: [{ id, sourceIdx, sourceName, pageRefs:[{source, page}], category, registration, vin,
 *          title, date, reference, confidence, summary, fields, vehicle }]
 * existing: vehicles already in the account [{ id, registration_number, vin, brand, model, year }]
 * Returns { items, proposals } where every item has vehicleKey ('v:<id>' | 'n:<REG>' | ''),
 * include, flags[] and target.
 */
export function buildPlan(docs, existing) {
  const byReg = {}, byVin = {};
  for (const v of existing) {
    if (v.registration_number) byReg[normReg(v.registration_number)] = 'v:' + v.id;
    if (v.vin && normVin(v.vin).length >= 8) byVin[normVin(v.vin)] = 'v:' + v.id;
  }

  let items = docs.map(d => ({
    ...d, registration: normReg(d.registration), vin: normVin(d.vin), flags: [], include: d.category !== 'blank',
    fields: { ...(d.fields || {}) },
    title: d.title || `${CATEGORY_LABELS[d.category] || 'Document'}${d.date ? ' ' + d.date.split('-').reverse().join('.') : ''}`,
  }));

  // 1. Registration / VIN matches. New registrations become car proposals.
  const newRegs = {};
  for (const it of items) {
    if (it.registration && byReg[it.registration]) it.vehicleKey = byReg[it.registration];
    else if (it.vin.length >= 8 && byVin[it.vin]) it.vehicleKey = byVin[it.vin];
    else if (it.registration.length >= 3 && it.category !== 'blank') {
      it.vehicleKey = 'n:' + it.registration;
      (newRegs[it.registration] = newRegs[it.registration] || []).push(it);
    } else it.vehicleKey = '';
  }
  const proposals = {};
  for (const [reg, ds] of Object.entries(newRegs)) proposals['n:' + reg] = proposalFrom(reg, ds);

  // 2. VIN-only documents (e.g. an MOT payment made before plates were issued).
  const vinToKey = { ...byVin };
  for (const p of Object.values(proposals)) if (p.vin.length >= 8) vinToKey[p.vin] = p.key;
  for (const it of items) {
    if (!it.vehicleKey && it.vin.length >= 8 && vinToKey[it.vin]) { it.vehicleKey = vinToKey[it.vin]; it.flags.push('Matched by chassis number'); }
  }

  // 3. Still unknown: assume the car most of the same file is about, and say so.
  const perSource = {};
  for (const it of items) (perSource[it.sourceIdx] = perSource[it.sourceIdx] || []).push(it);
  for (const group of Object.values(perSource)) {
    const guess = mostCommon(group.map(i => i.vehicleKey));
    for (const it of group) if (!it.vehicleKey && guess && it.category !== 'blank') { it.vehicleKey = guess; it.flags.push('Car guessed from the same file — check'); }
  }

  // 4. MOT certificate + result + payment slip from the same test → one record.
  const motGroups = {};
  for (const it of items) {
    if (it.category !== 'mot' || !it.vehicleKey) continue;
    const d = it.fields?.report_date || it.date;
    if (!d) continue;
    const k = it.vehicleKey + '|' + d;
    (motGroups[k] = motGroups[k] || []).push(it);
  }
  const absorbed = new Set();
  for (const group of Object.values(motGroups)) {
    if (group.length < 2) continue;
    const prio = { certificate: 0, result: 1, report: 2, payment: 3, photo: 4 };
    group.sort((a, b) => (prio[a.fields?.mot_part] ?? 5) - (prio[b.fields?.mot_part] ?? 5));
    const main = group[0];
    let merged = 0;
    for (const other of group.slice(1)) {
      // The same certificate scanned twice is a duplicate (step 5), not a part to merge.
      if (other.fields?.mot_part === 'certificate' && main.fields?.mot_part === 'certificate') continue;
      main.pageRefs = [...main.pageRefs, ...other.pageRefs];
      for (const [k, v] of Object.entries(other.fields || {})) if (main.fields[k] === undefined || main.fields[k] === '') main.fields[k] = v;
      if (other.fields?.mot_part === 'payment' && other.fields?.cost) main.fields.cost = other.fields.cost;
      absorbed.add(other.id);
      merged++;
    }
    if (merged) main.flags.push(`Combined ${merged + 1} MOT documents (certificate, result, payment) into one record`);
  }
  items = items.filter(it => !absorbed.has(it.id));

  // 5. Same document scanned twice → keep the first, untick the rest.
  const seen = {};
  for (const it of items) {
    if (!it.include || !it.vehicleKey) continue;
    const f = it.fields || {};
    const amount = f.amount ?? f.cost ?? f.amount_paid ?? '';
    const ref = it.reference || f.invoice_number || f.certificate_number || f.policy_number || '';
    const key = [it.vehicleKey, it.category, ref ? 'ref:' + ref.toUpperCase().replace(/\s/g, '') : `d:${it.date}|a:${amount}|${(it.title || '').toLowerCase()}`].join('|');
    if (seen[key]) { it.include = false; it.dupOf = seen[key].id; it.flags.push(`Duplicate of "${seen[key].title}" — not imported`); }
    else seen[key] = it;
  }

  for (const it of items) {
    if (it.confidence && it.confidence < 60) it.flags.push('Low confidence — check');
    if (it.include && !it.vehicleKey) it.flags.push('Pick the car');
    it.target = targetFor(it);
  }
  return { items, proposals };
}

// The database row for one item, once its file is uploaded. Matches what the app's own
// forms save so imported records behave exactly like hand-entered ones.
export function recordFor(item, { vehicleId, ownerId, userId, url, path }) {
  const f = item.fields || {};
  const notesBits = [item.summary, item.reference && `Ref ${item.reference}`].filter(Boolean);
  const notes = notesBits.join(' ') || null;
  switch (item.target) {
    case 'title_deeds':
      return { table: 'title_deeds', row: { vehicle_id: vehicleId, owner_name: f.owner_name || null, issue_date: f.issue_date || item.date || null, document_url: url, file_path: path, notes } };
    case 'insurance_records':
      return { table: 'insurance_records', row: { vehicle_id: vehicleId, insurance_company: f.insurance_company || null, policy_number: f.policy_number || null, start_date: f.start_date || null, expiry_date: f.expiry_date, cost: f.cost ?? null, insurance_type: f.insurance_type || null, document_url: url, notes } };
    case 'road_tax_records':
      return { table: 'road_tax_records', row: { vehicle_id: vehicleId, payment_date: f.payment_date || item.date || null, expiry_date: f.expiry_date, amount_paid: f.amount_paid ?? null, receipt_url: url, notes: [notes, f.licence_number && `Licence ${f.licence_number}`].filter(Boolean).join(' ') || null } };
    case 'mechanical_reports':
      return { table: 'mechanical_reports', row: { vehicle_id: vehicleId, report_date: f.report_date || item.date || null, next_due_date: f.next_due_date || null, garage_name: f.garage_name || null, mechanic_name: f.inspector_name || null, result: ['Passed', 'Failed', 'Needs Repair'].includes(f.result) ? f.result : 'Passed', cost: f.cost ?? null, document_url: url, notes: [notes, f.mileage && `Odometer ${f.mileage} km.`].filter(Boolean).join(' ') || null } };
    case 'expenses':
      return { table: 'expenses', row: { vehicle_id: vehicleId, user_id: ownerId, created_by: userId, expense_date: f.expense_date || item.date || new Date().toISOString().slice(0, 10), category: f.category || 'Other', description: f.description || item.title || null, amount: Number(f.amount), supplier: f.supplier || null, payment_method: f.payment_method || null, receipt_url: url, invoice_number: f.invoice_number || item.reference || null, notes, tags: Array.isArray(f.tags) ? f.tags : [], payment_status: 'paid' } };
    case 'documents': {
      const docType = { insurance: 'Insurance', road_tax: 'Road Tax', mot: 'MOT/KTEO', registration_foreign: 'Registration', expense: 'Invoice', import: 'Other', history: 'Other' }[item.category] || 'Other';
      return { table: 'documents', row: { vehicle_id: vehicleId, name: item.title, doc_type: docType, document_url: url, notes } };
    }
    default:
      return null;
  }
}

export const FOLDER_FOR = {
  title_deeds: 'title-deed', insurance_records: 'insurance', road_tax_records: 'road-tax',
  mechanical_reports: 'mechanical', expenses: 'receipts', documents: 'documents',
  classic_id: 'classic-id', immobilization: 'immobilization',
};
